// Thin fetch wrapper for the Netlify Functions API. Auth is handled by an HTTP-only
// cookie set by the /api/auth function, so we just need `credentials: 'include'`.

import type { AccessUser, ProjectsData, ResumeData } from './types';

const BASE = '/api';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
    ...init,
  });

  // 401 is meaningful to callers; throw a typed error rather than falling through.
  if (res.status === 401) {
    const err = new Error('Unauthorized') as Error & { status: number };
    err.status = 401;
    throw err;
  }

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`Request failed (${res.status}): ${body}`);
  }

  // Some endpoints return raw binary (PDF) — caller passes init.headers.Accept and reads .blob() themselves.
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}

export const api = {
  // --- Auth ---
  authenticate(password: string) {
    return request<{ ok: true }>(`/auth`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },
  adminAuthenticate(password: string) {
    return request<{ ok: true }>(`/admin-auth`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },
  logout() {
    return request<{ ok: true }>(`/logout`, { method: 'POST' });
  },

  // --- Resume ---
  getResume(opts: { for?: string } = {}) {
    const qs = opts.for ? `?for=${encodeURIComponent(opts.for)}` : '';
    return request<ResumeData>(`/resume${qs}`);
  },
  sendResume(to: string, from: string) {
    return request<{ mail: unknown; url: string }>(`/resume-send`, {
      method: 'POST',
      body: JSON.stringify({ email: to, from }),
    });
  },
  getPrintUrl() {
    return `${BASE}/resume-print`;
  },

  // --- Projects (portfolio gallery) ---
  // Auth-gated like /resume — the function returns 401 without a valid session.
  getProjects() {
    return request<ProjectsData>(`/projects`);
  },

  // --- Request access (public) ---
  requestAccess(email: string) {
    return request<{ user_exists?: boolean; request_sent?: boolean }>(`/user-request`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  // --- Admin ---
  listUsers() {
    return request<AccessUser[]>(`/users`);
  },
  approve(access_id: number, opts: { hide_summary?: boolean; send_email?: boolean } = {}) {
    return request<unknown>(`/user-approve`, {
      method: 'POST',
      body: JSON.stringify({
        access_id,
        hide_summary: opts.hide_summary || undefined,
        send_email: opts.send_email || undefined,
      }),
    });
  },
  revoke(access_id: number) {
    return request<unknown>(`/user-revoke`, {
      method: 'POST',
      body: JSON.stringify({ access_id }),
    });
  },
  deleteUser(id: number) {
    return request<unknown>(`/user-delete?id=${id}`, { method: 'DELETE' });
  },
  insertUser(email: string) {
    return request<{ password: string }>(`/user-insert`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },
};
